import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Share2, Link, Copy, Trash2, UserPlus, Globe, Lock, Check, Loader2, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareModal = ({ isOpen, onClose, project, onUpdate }) => {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('view');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publicLinkLoading, setPublicLinkLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [copied, setCopied] = useState(false);

  const sharedWith = project?.shared_with || [];
  const publicShareId = project?.public_share_id;
  const publicUrl = publicShareId ? `${window.location.origin}/public/${publicShareId}` : null;

  useEffect(() => {
    if (email.length >= 2) {
      searchUsers(email);
    } else {
      setSearchResults([]);
    }
  }, [email]);

  const searchUsers = async (query) => {
    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?query=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const shareWithUser = async (userEmail) => {
    setLoading(true);
    try {
      await axios.post(`${API}/projects/${project.id}/share`, {
        email: userEmail,
        permission
      });
      toast.success(`Shared with ${userEmail}`);
      setEmail('');
      setSearchResults([]);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setLoading(false);
    }
  };

  const removeShare = async (userId) => {
    try {
      await axios.delete(`${API}/projects/${project.id}/share/${userId}`);
      toast.success('Share removed');
      onUpdate();
    } catch (error) {
      toast.error('Failed to remove share');
    }
  };

  const createPublicLink = async () => {
    setPublicLinkLoading(true);
    try {
      await axios.post(`${API}/projects/${project.id}/share/public`);
      toast.success('Public link created');
      onUpdate();
    } catch (error) {
      toast.error('Failed to create public link');
    } finally {
      setPublicLinkLoading(false);
    }
  };

  const revokePublicLink = async () => {
    setPublicLinkLoading(true);
    try {
      await axios.delete(`${API}/projects/${project.id}/share/public`);
      toast.success('Public link revoked');
      onUpdate();
    } catch (error) {
      toast.error('Failed to revoke public link');
    } finally {
      setPublicLinkLoading(false);
    }
  };

  const copyPublicLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share Project
          </DialogTitle>
          <DialogDescription>
            Share this project with team members or create a public link
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Share with Users */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Share with people
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Input
                  placeholder="Enter email address..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pr-8"
                />
                {searching && (
                  <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                )}
              </div>
              <select
                className="border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={permission}
                onChange={(e) => setPermission(e.target.value)}
              >
                <option value="view">Can view</option>
                <option value="edit">Can edit</option>
              </select>
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full max-w-[280px] bg-white border border-slate-200 rounded-md shadow-lg">
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center justify-between"
                    onClick={() => shareWithUser(user.email)}
                    disabled={loading}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">{user.full_name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <UserPlus className="w-4 h-4 text-primary" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Current Shares */}
          {sharedWith.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Shared with
              </label>
              <div className="space-y-2">
                {sharedWith.map((share) => (
                  <div 
                    key={share.user_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-md"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {share.email?.[0]?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{share.email}</p>
                        <p className="text-xs text-slate-500 capitalize">{share.permission} access</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeShare(share.user_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Public Link Section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Public Link</span>
              </div>
              {publicShareId ? (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Active</span>
              ) : (
                <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-full">Disabled</span>
              )}
            </div>

            {publicShareId ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-md">
                  <Link className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-xs text-slate-600 truncate flex-1">{publicUrl}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyPublicLink}
                    className="flex-shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={revokePublicLink}
                  disabled={publicLinkLoading}
                  className="w-full text-red-600 border-red-200 hover:bg-red-50"
                >
                  {publicLinkLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  Revoke Public Link
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={createPublicLink}
                disabled={publicLinkLoading}
                className="w-full"
              >
                {publicLinkLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Globe className="w-4 h-4 mr-2" />
                )}
                Create Public Link
              </Button>
            )}
            <p className="text-xs text-slate-500 mt-2">
              Anyone with the public link can view this project (read-only)
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
